import { useState, useMemo, useCallback } from "react"
import { ScheduleOrderButton } from "@hydrologiq/shared/content/components/Button"
import { AssetItem } from "@hydrologiq/shared/components/activities/inputs/AvailableAssetCombobox"
import MultiScreenDialog, { Screen } from "@hydrologiq/shared/components/navigation/MultiScreenDialog"
import CustomerCreationForm from "@hydrologiq/shared/components/creation/CustomerCreationForm"
import CustomerSiteCreationForm from "@hydrologiq/shared/components/creation/CustomerSiteCreationForm"
import AssetCreationForm from "@hydrologiq/shared/components/creation/AssetCreationForm"
import FillingSiteCreationForm from "@hydrologiq/shared/components/creation/FillingSiteCreationForm"
import AssetAvailabilityCalendarWithData from "@hydrologiq/shared/components/calendar/AssetAvailabilityCalendarWithData"
import { OrderWorkflowProvider, useOrderWorkflowState, useOrderWorkflowActions } from "../contexts/OrderWorkflowContext"
import { mapsApiKey } from "../config"
import OrderFormContainer from "./OrderFormContainer"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"

interface OrderCreateProps {}

type ScreenId = "order" | "create-customer" | "create-site" | "create-asset" | "create-filling-site" | "calendar"

const OrderCreateInner = (_props: OrderCreateProps) => {
  const [open, setOpen] = useState(false)
  const [currentScreen, setCurrentScreen] = useState<ScreenId>("order")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
    [COPY_KEYS.HEADINGS.FILL_SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.FILL_SITE],
    [COPY_KEYS.BUTTONS.CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.CREATE],
    [COPY_KEYS.BUTTONS.SCHEDULE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.SCHEDULE],
    [COPY_KEYS.COMMON.A]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.A],
    [COPY_KEYS.COMMON.AN]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.AN],
  })

  const { resetWorkflow, openCalendar } = useOrderWorkflowActions()

  // Reset state when dialog closes
  const handleClose = useCallback(() => {
    setOpen(false)
    setCurrentScreen("order")
    resetWorkflow()
  }, [resetWorkflow])

  const handleBackToOrder = useCallback(() => {
    setCurrentScreen("order")
  }, [])

  // Handle navigation - no need for state parameter since parent manages state
  const handleNavigateToScreen = useCallback((screenId: ScreenId) => {
    setCurrentScreen(screenId)
  }, [])

  const handleOpenCalendar = useCallback((startDate: string, endDate: string, selectedAssets?: AssetItem[]) => {
    openCalendar(startDate, endDate, selectedAssets)
    setCurrentScreen("calendar")
  }, [])

  // Order form screen - use function to defer JSX creation until render time
  const renderOrderForm = useCallback(
    () => (
      <OrderFormContainer
        onSuccess={handleClose}
        onCancel={handleClose}
        onNavigateToScreen={handleNavigateToScreen}
        onOpenCalendar={handleOpenCalendar}
      />
    ),
    []
  )

  // Get actions at top level - never call hooks inside useMemo/useCallback
  // const state = useOrderWorkflowState()
  // const { selectCustomer, selectSite, addAsset } = useOrderWorkflowActions()

  const customerCreationScreen = useMemo(() => {
    const CustomerCreation = () => {
      const { selectCustomer, selectSite, setPendingCustomerName } = useOrderWorkflowActions()
      const state = useOrderWorkflowState()

      return (
        <CustomerCreationForm
          enableSiteCreation={true}
          initialName={state.pendingCustomerName}
          onSuccess={(customer) => {
            const sites = customer.sites ?? []
            selectCustomer({ id: customer.id, name: customer.name, sites: sites })
            if (sites.length > 0) {
              selectSite({ id: sites[0].id, name: sites[0].name })
            } else {
              selectSite(undefined)
            }
            // Clear the pending customer name after successful creation
            setPendingCustomerName("")
            handleBackToOrder()
          }}
          onBack={handleBackToOrder}
        />
      )
    }
    return <CustomerCreation />
  }, [handleBackToOrder])

  const siteCreationScreen = useMemo(() => {
    const SiteCreation = () => {
      const state = useOrderWorkflowState()
      const { selectSite } = useOrderWorkflowActions()

      return (
        <CustomerSiteCreationForm
          customerId={state.customer?.id || ""}
          mapsApiKey={mapsApiKey}
          onSuccess={(site) => {
            selectSite({ id: site.id, name: site.name })
            handleBackToOrder()
          }}
          onBack={handleBackToOrder}
          key={state.customer?.id}
        />
      )
    }
    return <SiteCreation />
  }, [handleBackToOrder])

  const assetCreationScreen = useMemo(() => {
    const AssetCreation = () => {
      const { addAsset } = useOrderWorkflowActions()

      return (
        <AssetCreationForm
          onSuccess={(asset) => {
            addAsset({ id: asset.id, name: asset.name })
            handleBackToOrder()
          }}
          onBack={handleBackToOrder}
        />
      )
    }
    return <AssetCreation />
  }, [handleBackToOrder])

  const fillingSiteCreationScreen = useMemo(
    () => (
      <FillingSiteCreationForm
        onSuccess={() => {
          // Filling site created, return to order screen
          handleBackToOrder()
        }}
        onBack={handleBackToOrder}
      />
    ),
    [handleBackToOrder]
  )

  const calendarScreen = useMemo(
    () => <AssetAvailabilityCalendarWithData onBack={handleBackToOrder} />,
    [handleBackToOrder]
  )

  const orderCopy = copy[COPY_KEYS.HEADINGS.ORDER]
  const isVowel = ["a", "e", "i", "o", "u"].includes(orderCopy[0].toLowerCase())
  const article = isVowel ? "an" : "a"

  const screens: Screen[] = useMemo(() => {
    return [
      {
        id: "order",
        title: `${copy[COPY_KEYS.BUTTONS.SCHEDULE]} ${article} ${orderCopy.toLowerCase()}`,
        canGoBack: false,
        component: renderOrderForm, // Function reference, not JSX
      },
      {
        id: "create-customer",
        title: `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.HEADINGS.CUSTOMER
        ].toLowerCase()}`,
        canGoBack: true,
        component: customerCreationScreen,
      },
      {
        id: "create-site",
        title: `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.HEADINGS.SITE
        ].toLowerCase()}`,
        canGoBack: true,
        component: siteCreationScreen,
      },
      {
        id: "create-asset",
        title: `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.AN]} ${copy[
          COPY_KEYS.HEADINGS.ASSET
        ].toLowerCase()}`,
        canGoBack: true,
        component: assetCreationScreen,
      },
      {
        id: "create-filling-site",
        title: `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.HEADINGS.FILL_SITE
        ].toLowerCase()}`,
        canGoBack: true,
        component: fillingSiteCreationScreen,
      },
      {
        id: "calendar",
        title: `${copy[COPY_KEYS.HEADINGS.ASSET]} Availability Calendar`,
        canGoBack: true,
        component: calendarScreen,
      },
    ]
  }, [
    renderOrderForm, // Now it's a stable function reference
    customerCreationScreen,
    siteCreationScreen,
    assetCreationScreen,
    fillingSiteCreationScreen,
    calendarScreen,
  ])

  return (
    <MultiScreenDialog
      screens={screens}
      currentScreenId={currentScreen}
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      onBack={handleBackToOrder}
      trigger={<ScheduleOrderButton />}
    />
  )
}

// Wrap with context provider
const OrderCreate = (props: OrderCreateProps) => {
  return (
    <OrderWorkflowProvider>
      <OrderCreateInner {...props} />
    </OrderWorkflowProvider>
  )
}

export default OrderCreate
